/**
 * Default pagination settings for conversation messages
 */
export const MESSAGE_PAGINATION_DEFAULTS = {
  totalMessages: 0,
  limit: 50,
  offset: 0,
};

/**
 * Number of conversations to fetch per page
 */
export const CONVERSATION_PAGE_LIMIT = 20;

/**
 * Default limit for fetching messages in a conversation
 */
export const DEFAULT_MESSAGE_LIMIT = 1000;

/**
 * Resolves a conversation ID or job ID to the actual conversation ID.
 * If the ID is a job ID that has been linked to a temporary conversation,
 * returns the temporary conversation ID. Otherwise, returns the original ID.
 *
 * @param {Object} state - The Vuex state object
 * @param {string} conversationIdOrJobId - Either a conversation ID or a job ID
 * @returns {string} The resolved conversation ID
 */
export function resolveTempId(state, conversationIdOrJobId) {
  return state.jobIdToTempId[conversationIdOrJobId] || conversationIdOrJobId;
}

/**
 * Builds an assistant message object from a job result.
 * Extracts the message content from the job output and formats it
 * according to the message structure expected by the UI.
 *
 * @param {Object} jobResult - The job result object from the backend
 * @returns {Object|null} The formatted message object, or null if no message found
 */
export function buildAssistantMessageFromJobResult(jobResult) {
  const messageOutput = jobResult.result?.output?.find(item => item.type === 'message');

  if (!messageOutput) {
    return null;
  }

  return {
    id: messageOutput.id,
    role: 'assistant',
    content: messageOutput.content?.[0]?.text || '',
    createdAt: new Date(jobResult.result.completedAt * 1000).toISOString(),
    metadata: {},
  };
}

/**
 * Builds a conversation object from a job result and temporary conversation data.
 * Combines information from the completed job with metadata from the temporary
 * conversation (like assetId and assetType) to create the final conversation object.
 *
 * @param {Object} jobResult - The job result object from the backend
 * @param {Object} tempConversation - The temporary conversation object
 * @returns {Object} The formatted conversation object
 */
export function buildConversationFromJobResult(jobResult, tempConversation) {
  return {
    id: jobResult.result?.conversationId,
    title: jobResult.result.output[0]?.queries?.[0]?.substring(0, 50) || 'New Conversation',
    aiEventId: jobResult.result.aiEventId || jobResult.result.metadata?.aiEventId,
    assetId: tempConversation?.assetId,
    assetType: tempConversation?.assetType,
    lastMessageAt: new Date(jobResult.result.completedAt * 1000).toISOString(),
    createdAt: new Date(jobResult.result.createdAt * 1000).toISOString(),
    metadata: {},
  };
}

/**
 * Creates a message pagination object with the specified total message count.
 * Uses default values for limit and offset.
 *
 * @param {number} totalMessages - The total number of messages
 * @returns {Object} A pagination object with totalMessages, limit, and offset
 */
export function createMessagePagination(totalMessages) {
  return {
    ...MESSAGE_PAGINATION_DEFAULTS,
    totalMessages,
  };
}

/**
 * Handles conversation-level error states with consistent behavior.
 * This is a shared helper for error handling actions that need to:
 * 1. Resolve the temporary conversation ID
 * 2. Commit an error-marking mutation
 * 3. Unlink the job ID if it exists
 * 4. Stop polling for job updates
 *
 * @param {Object} context - Vuex action context ({ commit, dispatch, state })
 * @param {string} conversationId - The conversation ID or job ID
 * @param {Function} mutationCallback - Callback function that commits the specific mutation
 */
export function handleConversationLevelError(context, conversationId, mutationCallback) {
  const { commit, dispatch, state } = context;
  const tempId = resolveTempId(state, conversationId);

  mutationCallback(tempId);

  if (state.jobIdToTempId[conversationId]) {
    commit('unlinkJobId', conversationId);
  }

  dispatch('mira/setPolling', false, { root: true });
}
